// 途中の致命 (normal) を「近すぎる時は外へ寄せてから」始める (2026-10-10 ユーザー採用・配布版の既定は step)。
//   出所 = docs/research/2026-10-10-execute-overlap-measure.md (戴冠獣 100%・覚醒王 90% が絵の半幅の内側で突く)
//          と docs/research/2026-10-10-exec-gap-status.md (この比較版の作り・測った数字)。
//   既定 = step (2026-10-10 ユーザー採用)。配布版は URL を読まないので常に step。旧 (normal は届く範囲の外にいる時だけ寄せる)
//   へ戻して見比べる時は DEV の ?execGap=0 (または off)。?execGap=ease は1拍を挟まない比較。
//
//   step (=1・推す案) : 寄せる量が大きい時は、3発の前に「後ろへ低く滑って間合いを取る1拍」を挟む。
//                       体は回避と同じ減速の曲線で下がり、絵は回避 (dodge_d) の低い姿。寄せ終わってから1発目を突く。
//                       1拍の長さは寄せる量に比例 (上限あり)。致命中はボスの崩れの時計が止まり、主人公は無敵なので
//                       1拍ぶん待っても戦いの規則は変わらない。小さい寄せは1拍を挟まず、今の 0.12 秒の寄せに混ぜる。
//   ease (比較用)     : 1拍を挟まず、3発の時間の中で寄せる時間だけ延ばす (距離に比例・上限あり)。1発目は寄せている途中で出る。
//
//   寄せる目標 = 見切り反撃・連撃3段目の止まり幅 (bosses.ts lungeStopGapPx) と揃える。持たないボスは
//   最後の致命の刺し距離 (finisherAnchorOffset)。どちらも届く範囲 (executeReachFor) − 10 で頭を押さえる (3発が必ず当たる)。
//   壁は越えない (player.ts の clampToArena を通した先までしか寄せない)。内側へは寄せない (外へだけ)。
import { EXECUTE, executeReachFor } from './balance'
import { bossCounterStopGap } from './bosses'

export type ExecuteSpacingMode = 'off' | 'step' | 'ease'

let mode: ExecuteSpacingMode = 'step'
/** DEV の URL の値をそのまま渡す。'0' / 'off' = 旧 (寄せない)・'ease' = 寄せる時間を延ばすだけ・'1' / 'step' = 1拍を挟む・
 *  null やそれ以外 = 既定の step のまま。 */
export function setExecuteSpacing(v: string | null): void {
  if (v === '0' || v === 'off') mode = 'off'
  else if (v === 'ease') mode = 'ease'
  else mode = 'step'
}
export function executeSpacingMode(): ExecuteSpacingMode { return mode }

/** これより小さい寄せは1拍を挟まない (今の 0.12 秒の寄せに混ぜても足の滑りが目立たない量) */
export const SPACING_PREP_MIN_PX = 24
/** 1px 寄せるのにかける時間 (ms)。回避 (90px を 260ms・減速の曲線) とほぼ同じ速さ */
export const SPACING_MS_PER_PX = 2.6
export const SPACING_MIN_MS = 140
export const SPACING_MAX_MS = 300

type SpacingBoss = { id: string; x: number; bodyHalfWidthPx: number; finisherAnchorOffset: number }

/** 途中の致命で、ボスの中心から主人公の中心までを最低これだけ空ける (px)。 */
export function executeSpacingGapPx(boss: SpacingBoss): number {
  const reachCap = executeReachFor(boss.bodyHalfWidthPx) - 10
  const stop = bossCounterStopGap(boss)
  const want = stop > 0 ? stop : boss.finisherAnchorOffset
  return Math.max(0, Math.min(want, reachCap))
}

export interface ExecuteSpacingPlan {
  anchorX: number
  fromX: number
  /** step の時だけ: 3発の前に挟む1拍の長さ (ms)。0 = 挟まない */
  prepMs: number
  /** 寄せにかける時間 (ms)。step で1拍を挟む時は prepMs と同じ */
  easeMs: number
}

/**
 * 途中の致命を始める時の寄せ方を決める。寄せない時は undefined (= 今の決まりへ任せる)。
 *   clamp = 壁の内側へ押さえる関数 (player.ts の clampToArena)。
 */
export function planExecuteSpacing(playerX: number, boss: SpacingBoss, clamp: (x: number) => number,
  m: ExecuteSpacingMode = mode): ExecuteSpacingPlan | undefined {
  if (m === 'off') return undefined
  const gap = executeSpacingGapPx(boss)
  const dist = Math.abs(boss.x - playerX)
  if (dist >= gap) return undefined
  const side = playerX <= boss.x ? -1 : 1
  const anchorX = clamp(boss.x + side * gap)
  // 壁で寄せ切れない時は、寄せられる所まで。今より内側 (ボス寄り) へは動かさない。
  const move = (anchorX - playerX) * side
  if (move <= 0.5) return undefined
  const easeMs = Math.max(SPACING_MIN_MS, Math.min(SPACING_MAX_MS, move * SPACING_MS_PER_PX))
  if (m === 'step' && move >= SPACING_PREP_MIN_PX) return { anchorX, fromX: playerX, prepMs: easeMs, easeMs }
  // 小さい寄せ (step) は今の 0.12 秒の寄せと同じ。ease は3発の時間の中で寄せる時間だけ延ばす。
  return { anchorX, fromX: playerX, prepMs: 0, easeMs: m === 'ease' ? easeMs : EXECUTE.anchorEaseMs }
}

/** 1拍 (prep) で下がる曲線 = 回避の描く位置と同じ「一瞬で出て減速する」(dodgeVisualLeadPx と同じ 2次の減速)。 */
export function spacingPrepProgress(t: number): number {
  const c = Math.max(0, Math.min(1, t))
  return 1 - (1 - c) * (1 - c)
}

// ── 最後の致命 (finisher) の寄せ方 (2026-10-18 ユーザー採用「配布版へ入れる」・配布版の既定は step)。
//   出所 = docs/research/2026-10-05-closed-bosses-fresh-eyes.md H4 (戴冠獣の最後の致命の始めに 0.1 秒で約 120px 跳ぶ)
//          と docs/research/2026-10-18-finisher-gap-trial.md (比較版の作り・測った数字)。
//   step = 途中の致命の step と同じ決まり。刺し距離 (finisherAnchorOffset) より内側から始めて外へ大きく寄せる時だけ、
//          回避の低い姿で後ろへ滑る1拍を挟んでから刺す。外から内へ寄せる時・小さい寄せは前のまま。
//   旧 (いつでも 0.12 秒で刺し距離へ寄せる) へ戻して見比べる時は DEV の ?finisherGap=0 (または off)。配布版は URL を読まないので常に step。
export type FinisherSpacingMode = 'off' | 'step'
let finisherMode: FinisherSpacingMode = 'step'
/** DEV の URL の値をそのまま渡す。'0' / 'off' = 旧 (0.12 秒の寄せ)。null やそれ以外 = 既定の step。 */
export function setFinisherSpacing(v: string | null): void {
  finisherMode = v === '0' || v === 'off' ? 'off' : 'step'
}
export function finisherSpacingMode(): FinisherSpacingMode { return finisherMode }

/** 最後の致命を始める時に1拍を挟むなら、その寄せ方を返す。挟まない時は undefined (= 0.12 秒の寄せへ任せる)。 */
export function planFinisherSpacing(playerX: number, boss: SpacingBoss, clamp: (x: number) => number,
  m: FinisherSpacingMode = finisherMode): ExecuteSpacingPlan | undefined {
  if (m === 'off') return undefined
  const side = playerX <= boss.x ? -1 : 1
  const anchorX = clamp(boss.x + side * boss.finisherAnchorOffset)
  const move = (anchorX - playerX) * side
  if (move < SPACING_PREP_MIN_PX) return undefined
  const easeMs = Math.max(SPACING_MIN_MS, Math.min(SPACING_MAX_MS, move * SPACING_MS_PER_PX))
  return { anchorX, fromX: playerX, prepMs: easeMs, easeMs }
}
